import { memo, type ReactNode } from 'react';
import { StyleSheet, View, type StyleProp, type ViewStyle } from 'react-native';
import { useTheme } from '../../hooks/useTheme';

/**
 * Shared "list card" surface: the square-but-rounded container that wraps a
 * scrollable list. Used as a single wrapping View on the Notifications screen
 * (FlatList inside the card) and assembled out of individual rows on the Jobs
 * feed via {@link getGroupedRowStyle} (its list header scrolls with the rows,
 * so the card can't be one wrapping View). Centralising the radius/surface/
 * border tokens here keeps both screens: and their skeletons: pixel-identical.
 */
export const LIST_CARD_RADIUS = 25;

export interface ListCardColors {
  /** Card fill: a faint off-white island on light, an elevated surface on dark. */
  surface: string;
  /** Outer card edge. */
  border: string;
  /** Hairline divider between adjacent rows (lighter than the outer edge). */
  separator: string;
}

/**
 * Theme-aware card colours. Light mode reproduces the original Notifications
 * card exactly (#fafafa fill, #e9e9e9 dividers); dark mode swaps to elevated
 * surface tokens so the card reads correctly against a black background.
 */
export function useListCardColors(): ListCardColors {
  const { theme, isDark } = useTheme();
  if (isDark) {
    return {
      surface: theme.surface,
      border: theme.border,
      separator: theme.borderSubtle,
    };
  }
  return {
    surface: '#fafafa',
    border: theme.border,
    separator: '#e9e9e9',
  };
}

interface ListCardProps {
  children: ReactNode;
  style?: StyleProp<ViewStyle>;
}

function ListCardImpl({ children, style }: ListCardProps) {
  const colors = useListCardColors();
  return (
    <View
      style={[
        styles.card,
        { backgroundColor: colors.surface, borderColor: colors.border },
        style,
      ]}
    >
      {children}
    </View>
  );
}

export const ListCard = memo(ListCardImpl);

/**
 * Per-row container style that assembles a continuous rounded ListCard out of
 * individual FlatList rows: the first/last rows round the top/bottom corners,
 * outer edges use the card border, and the divider between adjacent rows uses
 * the lighter separator. A single-row group (total === 1) rounds all corners.
 */
export function getGroupedRowStyle(
  index: number,
  total: number,
  colors: ListCardColors,
): ViewStyle {
  const isFirst = index <= 0;
  const isLast = index >= total - 1;
  return {
    backgroundColor: colors.surface,
    overflow: 'hidden',
    borderLeftWidth: StyleSheet.hairlineWidth,
    borderRightWidth: StyleSheet.hairlineWidth,
    borderTopWidth: isFirst ? StyleSheet.hairlineWidth : 0,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderLeftColor: colors.border,
    borderRightColor: colors.border,
    borderTopColor: colors.border,
    borderBottomColor: isLast ? colors.border : colors.separator,
    borderTopLeftRadius: isFirst ? LIST_CARD_RADIUS : 0,
    borderTopRightRadius: isFirst ? LIST_CARD_RADIUS : 0,
    borderBottomLeftRadius: isLast ? LIST_CARD_RADIUS : 0,
    borderBottomRightRadius: isLast ? LIST_CARD_RADIUS : 0,
  };
}

const styles = StyleSheet.create({
  card: {
    borderRadius: LIST_CARD_RADIUS,
    borderWidth: 1,
    overflow: 'hidden',
  },
});
